/**
 * CSV / TSV parser with header-based column auto-detection. Handles the
 * common column names emitted by ForeFlight, Stratux, Garmin Pilot,
 * CloudAhoy, and hand-rolled spreadsheets.
 *
 * Recognised column aliases (case-insensitive):
 *   latitude      : lat, latitude, latdeg, lat_deg, y
 *   longitude     : lon, lng, long, longitude, londeg, lon_deg, x
 *   altitude      : alt, altitude, altitude_ft, altitude_m, ele, elevation, alt_ft, alt_m
 *   ground speed  : gs, groundspeed, ground_speed, speed, speed_kts, speed_mps
 *   heading       : hdg, heading, course, track, bearing
 *   timestamp     : time, timestamp, datetime, epoch, utc, iso_time
 *
 * Altitude / speed are detected in feet / knots unless the column name
 * explicitly says `_m` / `_mps`, in which case the value is converted.
 */
import type { TrackPoint } from './track-types';

const M_TO_FT = 3.28084;
const MPS_TO_KTS = 1.94384;
const KMH_TO_KTS = 0.539957;
const MPH_TO_KTS = 0.868976;

interface ColumnMap {
  lat: number;
  lon: number;
  alt?: number;
  altInMeters?: boolean;
  speed?: number;
  speedUnit?: 'kts' | 'mps' | 'kmh' | 'mph';
  heading?: number;
  time?: number;
}

function normaliseHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function detectColumns(headers: string[]): ColumnMap | null {
  const map: Partial<ColumnMap> = {};
  headers.forEach((rawH, i) => {
    const h = normaliseHeader(rawH);
    if (map.lat === undefined && /^(lat|latitude|lat_deg|y)$/.test(h)) map.lat = i;
    else if (map.lon === undefined && /^(lon|lng|long|longitude|lon_deg|x)$/.test(h)) map.lon = i;
    else if (map.alt === undefined && /^(alt|altitude|ele|elevation|alt_ft|altitude_ft)$/.test(h)) { map.alt = i; map.altInMeters = false; }
    else if (map.alt === undefined && /^(alt_m|altitude_m|elevation_m)$/.test(h)) { map.alt = i; map.altInMeters = true; }
    else if (map.speed === undefined && /^(gs|groundspeed|ground_speed|speed|speed_kts|speed_kt)$/.test(h)) { map.speed = i; map.speedUnit = 'kts'; }
    else if (map.speed === undefined && /^(speed_mps|speed_ms|gs_mps)$/.test(h)) { map.speed = i; map.speedUnit = 'mps'; }
    else if (map.speed === undefined && /^(speed_kmh|speed_kph)$/.test(h)) { map.speed = i; map.speedUnit = 'kmh'; }
    else if (map.speed === undefined && /^(speed_mph)$/.test(h)) { map.speed = i; map.speedUnit = 'mph'; }
    else if (map.heading === undefined && /^(hdg|heading|course|track|bearing|track_deg|course_deg)$/.test(h)) map.heading = i;
    else if (map.time === undefined && /^(time|timestamp|datetime|epoch|utc|iso_time)$/.test(h)) map.time = i;
  });
  if (map.lat === undefined || map.lon === undefined) return null;
  return map as ColumnMap;
}

function parseTime(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  // Pure number \u2192 epoch seconds or milliseconds
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const n = parseFloat(trimmed);
    if (n > 1e12) return n;          // ms
    if (n > 1e9)  return n * 1000;   // s \u2192 ms
    return undefined;
  }
  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (c === delimiter && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

function convertSpeed(value: number, unit: ColumnMap['speedUnit']): number {
  switch (unit) {
    case 'mps': return value * MPS_TO_KTS;
    case 'kmh': return value * KMH_TO_KTS;
    case 'mph': return value * MPH_TO_KTS;
    case 'kts':
    default:    return value;
  }
}

export function parseCsv(content: string, delimiter = ','): { name: string | undefined; points: TrackPoint[] } {
  const lines = content.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim().length > 0);
  if (lines.length < 2) throw new Error('CSV has no data rows');

  const header = splitCsvLine(lines[0], delimiter).map((h) => h.replace(/^"|"$/g, ''));
  const cols = detectColumns(header);
  if (!cols) throw new Error('CSV missing latitude/longitude columns (saw: ' + header.join(', ') + ')');

  const points: TrackPoint[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCsvLine(lines[i], delimiter).map((c) => c.replace(/^"|"$/g, ''));
    const lat = parseFloat(cells[cols.lat]);
    const lon = parseFloat(cells[cols.lon]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const pt: TrackPoint = { latitude: lat, longitude: lon };
    if (cols.alt !== undefined) {
      const v = parseFloat(cells[cols.alt]);
      if (Number.isFinite(v)) pt.altitudeFt = cols.altInMeters ? v * M_TO_FT : v;
    }
    if (cols.speed !== undefined) {
      const v = parseFloat(cells[cols.speed]);
      if (Number.isFinite(v)) pt.groundSpeedKts = convertSpeed(v, cols.speedUnit);
    }
    if (cols.heading !== undefined) {
      const v = parseFloat(cells[cols.heading]);
      if (Number.isFinite(v)) pt.heading = v;
    }
    if (cols.time !== undefined) {
      const t = parseTime(cells[cols.time]);
      if (t !== undefined) pt.timestamp = t;
    }
    points.push(pt);
  }
  return { name: undefined, points };
}

export function parseTsv(content: string): { name: string | undefined; points: TrackPoint[] } {
  return parseCsv(content, '\t');
}
